/**
 * Staff Assignments Dashboard Component
 * Shows all proposals with assigned staff, assignment statistics, and bulk assignment features
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Badge, LoadingSpinner, EmptyState, Modal } from '@tracekit/core';
import { useWorkspace, useToastStore } from '@tracekit/core';
import { listReports, getReport } from '@tracekit/core';
import { getReportStaffAssignments, assignStaffToReport, removeStaffAssignment } from '../services/rfpApi';
import { StaffAssignmentModal } from './StaffAssignmentModal';
import {
  Users,
  UserPlus,
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
  FileText,
  X,
  Trash2,
} from 'lucide-react';

export function StaffAssignmentsDashboard() {
  const { projectId } = useWorkspace();
  const { addToast } = useToastStore();
  const queryClient = useQueryClient();
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState<'all' | 'assigned' | 'unassigned'>('all');

  // Fetch all reports (proposals) - use same query key as ComplianceReports to share cache
  const { data: reports, isLoading: reportsLoading, error: reportsError } = useQuery({
    queryKey: ['reports', projectId],
    queryFn: () => listReports(projectId!),
    enabled: !!projectId,
  });

  // Filter for RFP proposals - match the same logic as ComplianceReports
  const rfpProposals = useMemo(() => {
    if (!reports) return [];
    return reports.filter((report: any) => {
      // Handle both 'metadata' and 'meta' fields (Report type uses 'meta', but some code uses 'metadata')
      const metadata = report.metadata || report.meta || {};
      
      // Match the same filtering logic as ComplianceReports and ReviewWorkflowPage
      // Check for both report_type='rfp_proposal' OR custom with override
      const isRfpProposal = 
        report.report_type === 'rfp_proposal' ||
        (report.report_type === 'custom' && 
         (metadata.report_type_override === 'rfp_proposal' || metadata.rfp_artifact_id));
      
      return isRfpProposal;
    });
  }, [reports]);
  
  // Debug logging to help diagnose filtering issues
  useEffect(() => {
    if (reports) {
      console.log('[StaffAssignmentsDashboard] Reports loaded:', {
        total: reports.length,
        rfpProposals: rfpProposals.length,
        sampleReport: reports[0] ? {
          id: reports[0].id,
          report_type: reports[0].report_type,
          metadata: (reports[0] as any).metadata,
          meta: (reports[0] as any).meta,
          name: (reports[0] as any).name,
          client_name: (reports[0] as any).client_name,
        } : null,
      });
    }
  }, [reports, rfpProposals.length]);

  // Fetch assignments for all proposals
  const assignmentQueries = useQuery({
    queryKey: ['all-report-assignments', rfpProposals.map((r: any) => r.id)],
    queryFn: async () => {
      const assignments: Record<string, any> = {};
      for (const proposal of rfpProposals) {
        try {
          const data = await getReportStaffAssignments(proposal.id);
          assignments[proposal.id] = data;
        } catch (error) {
          // Ignore errors for proposals without assignments
          assignments[proposal.id] = { assignments: [], total_assignments: 0 };
        }
      }
      return assignments;
    },
    enabled: rfpProposals.length > 0,
  });

  const assignmentsData = assignmentQueries.data || {};

  // Calculate statistics
  const stats = useMemo(() => {
    const totalProposals = rfpProposals.length;
    const proposalsWithAssignments = Object.values(assignmentsData).filter(
      (data: any) => data.total_assignments > 0
    ).length;
    const totalAssignments = Object.values(assignmentsData).reduce(
      (sum: number, data: any) => sum + (data.total_assignments || 0),
      0
    );
    const uniqueStaff = new Set(
      Object.values(assignmentsData).flatMap((data: any) =>
        data.assignments?.map((a: any) => a.staff_member_id) || []
      )
    ).size;

    return {
      totalProposals,
      proposalsWithAssignments,
      proposalsWithoutAssignments: totalProposals - proposalsWithAssignments,
      totalAssignments,
      uniqueStaff,
    };
  }, [rfpProposals, assignmentsData]);

  // Filter proposals
  const filteredProposals = useMemo(() => {
    if (filterStatus === 'all') return rfpProposals;
    if (filterStatus === 'assigned') {
      return rfpProposals.filter((p: any) => (assignmentsData[p.id]?.total_assignments || 0) > 0);
    }
    return rfpProposals.filter((p: any) => (assignmentsData[p.id]?.total_assignments || 0) === 0);
  }, [rfpProposals, assignmentsData, filterStatus]);

  const handleAssign = (reportId: string) => {
    setSelectedReportId(reportId);
    setShowAssignModal(true);
  };

  const handleRemoveAssignment = async (reportId: string, assignmentId: string) => {
    if (confirm('Remove this staff assignment?')) {
      try {
        await removeStaffAssignment(assignmentId);
        // Invalidate all assignment queries to refresh counts everywhere
        queryClient.invalidateQueries({ queryKey: ['all-report-assignments'] });
        queryClient.invalidateQueries({ queryKey: ['report-staff-assignments'] });
        queryClient.invalidateQueries({ queryKey: ['staff-capacity'] });
        queryClient.invalidateQueries({ queryKey: ['staff-availability'] });
        addToast({ message: 'Assignment removed', type: 'success' });
      } catch (error: any) {
        addToast({
          message: error.response?.data?.detail || 'Failed to remove assignment',
          type: 'error',
        });
      }
    }
  };

  if (reportsLoading || assignmentQueries.isLoading) {
    return (
      <Card className="p-8">
        <div className="flex items-center justify-center">
          <LoadingSpinner />
          <span className="ml-3 text-gray-600">Loading staff assignments...</span>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8 text-blue-600" />
            <div>
              <div className="text-2xl font-bold">{stats.totalProposals}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Total Proposals</div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-green-600" />
            <div>
              <div className="text-2xl font-bold">{stats.proposalsWithAssignments}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">With Staff Assigned</div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <UserPlus className="w-8 h-8 text-purple-600" />
            <div>
              <div className="text-2xl font-bold">{stats.totalAssignments}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Total Assignments</div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-orange-600" />
            <div>
              <div className="text-2xl font-bold">{stats.uniqueStaff}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Unique Staff Members</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Proposals</h3>
          <div className="flex gap-2">
            <Button
              variant={filterStatus === 'all' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setFilterStatus('all')}
            >
              All ({stats.totalProposals})
            </Button>
            <Button
              variant={filterStatus === 'assigned' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setFilterStatus('assigned')}
            >
              Assigned ({stats.proposalsWithAssignments})
            </Button>
            <Button
              variant={filterStatus === 'unassigned' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setFilterStatus('unassigned')}
            >
              Unassigned ({stats.proposalsWithoutAssignments})
            </Button>
          </div>
        </div>
      </Card>

      {/* Proposals List */}
      {filteredProposals.length === 0 ? (
        <Card className="p-8">
          <EmptyState
            icon={<FileText className="w-12 h-12" />}
            title="No Proposals Found"
            description={
              reportsLoading
                ? 'Loading proposals...'
                : reportsError
                ? 'Error loading proposals. Please refresh the page.'
                : rfpProposals.length === 0
                ? `No RFP proposals found. Total reports loaded: ${reports?.length || 0}. Create proposals in the "Current Proposals" tab.`
                : filterStatus === 'unassigned'
                ? 'All proposals have staff assigned'
                : filterStatus === 'assigned'
                ? 'No proposals with staff assignments'
                : 'No proposals match the current filter'
            }
          />
          {!reportsLoading && !reportsError && rfpProposals.length === 0 && reports && reports.length > 0 && (
            <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg text-sm">
              <p className="font-semibold mb-2">Debug Info:</p>
              <p>Total reports loaded: {reports.length}</p>
              <p>RFP proposals filtered: {rfpProposals.length}</p>
              <details className="mt-2">
                <summary className="cursor-pointer text-blue-600 dark:text-blue-400">Sample report structure</summary>
                <pre className="mt-2 p-2 bg-gray-100 dark:bg-gray-900 rounded text-xs overflow-auto">
                  {JSON.stringify(reports[0] ? {
                    id: reports[0].id,
                    report_type: reports[0].report_type,
                    name: (reports[0] as any).name,
                    client_name: (reports[0] as any).client_name,
                    metadata: (reports[0] as any).metadata,
                    meta: (reports[0] as any).meta,
                  } : null, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredProposals.map((proposal: any) => {
            const assignments = assignmentsData[proposal.id]?.assignments || [];
            const assignmentCount = assignments.length;

            return (
              <Card key={proposal.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                        {proposal.name || proposal.client_name}
                      </h4>
                      <Badge variant={assignmentCount > 0 ? 'default' : 'secondary'}>
                        {assignmentCount} staff assigned
                      </Badge>
                      <Badge variant="outline">{proposal.status}</Badge>
                    </div>
                    
                    {assignmentCount > 0 && (
                      <div className="mt-3 space-y-2">
                        <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Assigned Staff:
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {assignments.map((assignment: any) => (
                            <Badge
                              key={assignment.id}
                              variant="outline"
                              className="flex items-center gap-2"
                            >
                              <span>{assignment.staff_name || 'Unknown'}</span>
                              {assignment.role && (
                                <span className="text-xs text-gray-500">({assignment.role})</span>
                              )}
                              <button
                                onClick={() => handleRemoveAssignment(proposal.id, assignment.id)}
                                className="ml-1 hover:text-red-600"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAssign(proposal.id)}
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      {assignmentCount > 0 ? 'Add Staff' : 'Assign Staff'}
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Assign Staff Modal */}
      {showAssignModal && selectedReportId && (
        <StaffAssignmentModal
          reportId={selectedReportId}
          reportName={rfpProposals.find((r: any) => r.id === selectedReportId)?.name}
          projectId={projectId || ''}
          isOpen={showAssignModal}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedReportId(null);
          }}
          onSuccess={() => {
            // Invalidate all assignment queries to refresh counts and lists
            queryClient.invalidateQueries({ queryKey: ['all-report-assignments'] });
            queryClient.invalidateQueries({ queryKey: ['report-staff-assignments'] });
            queryClient.invalidateQueries({ queryKey: ['staff-capacity'] });
            queryClient.invalidateQueries({ queryKey: ['staff-availability'] });
          }}
        />
      )}
    </div>
  );
}
